import { Helmet } from "react-helmet-async";
import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { liveSocialLinks } from "@/config/socialLinks";
import { supabase } from "@/integrations/supabase/client";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { usePushNotifications, type PushTopic } from "@/hooks/usePushNotifications";
import { useEmailSubscription, type EmailTopic } from "@/hooks/useEmailSubscription";
import { blogPosts } from "@/data/blogPosts";
import { trackEmailSubscribe, trackEmailUnsubscribe, trackPushSubscribe, trackPushUnsubscribe } from "@/utils/analytics";
import { US_STATES } from "@/components/admin/social/constants";
import {
  RESOURCES,
  CATEGORY_ORDER,
  CATEGORY_META,
  matchResources,
  type CareResource,
  type ResourceCategory,
} from "@/components/scenarios/ScenarioCareResources";
import {
  Bell,
  BellRing,
  Mail,
  Shield,
  Search,
  TrendingUp,
  Database,
  Users,
  ExternalLink,
  ChevronRight,
  Star,
  Newspaper,
  Scale,
  BarChart3,
  HeartHandshake,
  MapPin,
  BookOpen,
  X,
  Phone,
  DollarSign,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

// ── State name lookup ─────────────────────────────────────────────────────────
const STATE_NAMES: Record<string, string> = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',DC:'District of Columbia',FL:'Florida',GA:'Georgia',
  HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',
  LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',
  MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',
  NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',
  OH:'Ohio',OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',
  SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',
  WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',
};

// ── Compliance constants ─────────────────────────────────────────────────────
const LAW_EFFECTIVE = new Date('2021-01-01');
const TODAY = new Date();
const DAYS_LAW_ACTIVE = Math.floor(
  (TODAY.getTime() - LAW_EFFECTIVE.getTime()) / (1000 * 60 * 60 * 24)
);
const EST_FINE_GAP_TOTAL = 3000 * 2000 * DAYS_LAW_ACTIVE;
const CMS_COLLECTED = 1_500_000;

// ── Blog icon map ───────────────────────────────────────────────────────────
const ICON_MAP: Record<string, React.ElementType> = {
  Shield, Scale, BarChart3, TrendingUp, Users, BookOpen, Newspaper,
};

// ── Category icons for kanban ────────────────────────────────────────────────
const KANBAN_ICONS: Record<ResourceCategory, React.ElementType> = {
  financial: DollarSign,
  program: ShieldCheck,
  peer: Users,
  advocacy: HeartHandshake,
};

// ── Kanban colors (dark-mode friendly) ──────────────────────────────────────
const KANBAN_COLORS: Record<ResourceCategory, { light: string; dark: string; accent: string }> = {
  financial: { light: '#059669', dark: '#34d399', accent: '#10b981' },
  program:   { light: '#2563eb', dark: '#60a5fa', accent: '#3b82f6' },
  peer:      { light: '#7c3aed', dark: '#a78bfa', accent: '#8b5cf6' },
  advocacy:  { light: '#dc2626', dark: '#f87171', accent: '#ef4444' },
};

// ── Animated resource card ──────────────────────────────────────────────────
function ResourceCard({
  resource,
  color,
  isDark,
  index,
  animate,
}: {
  resource: CareResource;
  color: string;
  isDark: boolean;
  index: number;
  animate: boolean;
}) {
  const { t } = useTranslation('common');
  const [visible, setVisible] = useState(!animate);

  useEffect(() => {
    if (!animate) { setVisible(true); return; }
    const t = setTimeout(() => setVisible(true), 60 + index * 80);
    return () => clearTimeout(t);
  }, [animate, index]);

  return (
    <div
      className="rounded-lg p-3 transition-all duration-300"
      style={{
        backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(12px)',
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span
          className="text-sm font-semibold leading-tight"
          style={{ color: isDark ? '#f0f0f0' : '#1a1a1a' }}
        >
          {resource.name}
        </span>
        {resource.states && resource.states.length > 0 && (
          <span
            className="flex-shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
            style={{ backgroundColor: `${color}20`, color }}
          >
            <MapPin className="w-2 h-2" />
            {resource.states.join(', ')}
          </span>
        )}
      </div>
      {resource.org && (
        <div className="text-[11px] mb-1" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
          {resource.org}
        </div>
      )}
      <p
        className="text-xs leading-relaxed mb-2"
        style={{ color: isDark ? '#b0b0b0' : '#555' }}
      >
        {resource.description}
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <a
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-medium flex items-center gap-1 hover:underline"
          style={{ color }}
        >
          <ExternalLink className="w-3 h-3" />
          {t('follow.care.visit')}
        </a>
        {resource.phone && (
          <a
            href={`tel:${resource.phone.replace(/\D/g, '')}`}
            className="text-xs font-medium flex items-center gap-1 hover:underline"
            style={{ color: isDark ? '#9ca3af' : '#6b7280' }}
          >
            <Phone className="w-3 h-3" />
            {resource.phone}
          </a>
        )}
      </div>
    </div>
  );
}

// ── Main Follow page ────────────────────────────────────────────────────────
const Follow = () => {
  const { t } = useTranslation('common');
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';
  const searchRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // ── Design tokens ─────────────────────────────────────────────────────────
  const cream  = isDark ? '#0a1628' : '#FAF9F6';
  const navy   = isDark ? '#7da6d4' : '#002868';
  const red    = isDark ? '#e85d6c' : '#B22234';
  const gold   = '#C5A55A';
  const green  = isDark ? '#6bc9a0' : '#2b5e49';
  const ink    = isDark ? '#f0ebe3' : '#1B1B1B';
  const muted  = isDark ? '#9ca3af' : '#6b7280';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const cardBg = isDark ? '#111827' : '#fff';
  const serif  = "'Instrument Serif', Georgia, serif";
  const sans   = "'Instrument Sans', system-ui, sans-serif";

  // ── Push notifications ───────────────────────────────────────────────────
  const {
    isSupported: pushSupported,
    permission: pushPermission,
    isSubscribed,
    isLoading: pushLoading,
    error: pushError,
    subscribe: pushSubscribe,
    unsubscribe: pushUnsubscribe,
    updatePreferences: pushUpdatePreferences,
  } = usePushNotifications();
  const [pushState, setPushState] = useState<string>('');
  const [pushTopics, setPushTopics] = useState<Record<PushTopic, boolean>>({
    compliance: true,
    pricing: true,
    legislation: true,
    general: true,
  });

  // ── Scenario search for care resources ────────────────────────────────────
  const [scenarioSearch, setScenarioSearch] = useState('');
  const [selectedScenario, setSelectedScenario] = useState<{ id: string; name: string; tags: string[] } | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [animateResources, setAnimateResources] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  const { data: scenarioResults = [] } = useQuery({
    queryKey: ['follow-scenario-search', scenarioSearch],
    queryFn: async () => {
      if (!scenarioSearch || scenarioSearch.length < 2) return [];
      const { data, error } = await supabase
        .from('scenarios')
        .select('id, name, tags')
        .ilike('name', `%${scenarioSearch}%`)
        .limit(8);
      if (error) return [];
      return data as { id: string; name: string; tags: string[] }[];
    },
    enabled: scenarioSearch.length >= 2 && !selectedScenario,
  });

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node) &&
        searchRef.current && !searchRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Email subscription ──────────────────────────────────────────────────
  const {
    isSubscribed: emailSubscribed,
    isLoading: emailLoading,
    error: emailError,
    subscribe: emailSubscribe,
    unsubscribe: emailUnsubscribe,
    updatePreferences: emailUpdatePreferences,
  } = useEmailSubscription();
  const [email, setEmail] = useState('');
  const [emailState, setEmailState] = useState<string>('');
  const [emailTopics, setEmailTopics] = useState<Record<EmailTopic, boolean>>({
    compliance: true,
    pricing: true,
    legislation: true,
    general: true,
  });

  // Resources for kanban
  const resources = useMemo(() => {
    if (!selectedScenario) return matchResources(['general'], 'General');
    return matchResources(selectedScenario.tags, selectedScenario.name);
  }, [selectedScenario]);

  const grouped = useMemo(() => {
    return CATEGORY_ORDER.reduce<Record<ResourceCategory, CareResource[]>>(
      (acc, cat) => {
        acc[cat] = resources.filter(r => r.category === cat);
        return acc;
      },
      { peer: [], financial: [], program: [], advocacy: [] }
    );
  }, [resources]);

  const handleSelectScenario = (s: { id: string; name: string; tags: string[] }) => {
    setSelectedScenario(s);
    setScenarioSearch('');
    setShowSuggestions(false);
    setAnimateResources(true);
    setTimeout(() => setAnimateResources(false), 2000);
  };

  const handleClearScenario = () => {
    setSelectedScenario(null);
    setScenarioSearch('');
    setAnimateResources(true);
    setTimeout(() => setAnimateResources(false), 2000);
  };

  const canonicalUrl = `${brand.primaryDomain}/follow`;

  return (
    <div className="min-h-screen pb-24 md:pb-8" style={{ backgroundColor: cream, color: ink }}>
      <Helmet>
        <title>{t(isAhp ? 'follow.meta.title' : 'follow.metaAlt.title', { brandName: brand.name })}</title>
        <meta name="description" content={t('follow.meta.description')} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      {/* ═══════════════════════════════════════════════════════════════════════
          A. HERO — "Let Dignity Ring"
      ═══════════════════════════════════════════════════════════════════════ */}
      <section
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${isDark ? '#0a1628' : '#002868'} 0%, ${isDark ? '#060e1a' : '#001540'} 100%)` }}
      >
        <div className="mx-auto max-w-4xl px-6 py-16 md:py-20 text-center relative z-10">
          <div className="mx-auto mb-6 w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: `${gold}20`, border: `2px solid ${gold}50` }}>
            <BellRing className="w-10 h-10" style={{ color: gold }} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: serif, color: '#fff' }}>
            {/* "Let Dignity Ring" is AHP's mascot pun; siblings get a neutral line. */}
            {t(isAhp ? 'follow.hero.title' : 'follow.heroAlt.title')}
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto mb-6" style={{ fontFamily: sans, color: 'rgba(255,255,255,0.75)' }}>
            {t('follow.hero.subtitle')}
          </p>
          <div className="flex justify-center gap-2 mb-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-current" style={{ color: gold }} />
            ))}
          </div>
        </div>
        <div className="h-1" style={{ background: `linear-gradient(to right, ${red}, ${red} 33%, white 33%, white 66%, ${red} 66%)` }} />
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          A2. WHERE TO FOLLOW — the accounts that actually exist. Sourced from
          config/socialLinks.ts so a handle is listed in exactly one place, and
          unverified accounts never render (a dead link costs more credibility
          here than a missing one).
      ═══════════════════════════════════════════════════════════════════════ */}
      {liveSocialLinks().length > 0 && (
        <section className="mx-auto max-w-4xl px-6 pt-12">
          <h2 className="text-2xl font-bold mb-2 text-center" style={{ fontFamily: serif, color: ink }}>
            Follow the work
          </h2>
          <p className="text-center text-sm mb-6" style={{ fontFamily: sans, color: isDark ? 'rgba(246,241,229,0.6)' : 'rgba(27,27,27,0.6)' }}>
            Short lessons on what care actually costs — one procedure at a time.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {liveSocialLinks().map((s) => (
              <a
                key={s.id}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-xl p-5 flex items-center gap-4 transition-transform hover:-translate-y-0.5"
                style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${s.color}15`, color: s.color }}
                >
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true">
                    <path d={s.path} />
                  </svg>
                </div>
                <div className="min-w-0">
                  <div className="font-semibold" style={{ fontFamily: serif, color: ink }}>{s.label}</div>
                  <div className="text-sm truncate" style={{ fontFamily: sans, color: isDark ? 'rgba(246,241,229,0.6)' : 'rgba(27,27,27,0.6)' }}>
                    {s.handle}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          B. STAY UPDATED — Notifications & Email
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-4xl px-6 py-12">
        <h2 className="text-2xl font-bold mb-6 text-center" style={{ fontFamily: serif, color: ink }}>
          {t('follow.stayUpdated')}
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-xl p-6" style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${red}15` }}>
                {isSubscribed ? <BellRing className="w-5 h-5" style={{ color: green }} /> : <Bell className="w-5 h-5" style={{ color: red }} />}
              </div>
              <div>
                <h3 className="font-semibold text-sm" style={{ fontFamily: sans, color: ink }}>
                  {t('follow.push.title')}
                </h3>
                <p className="text-xs" style={{ color: muted }}>
                  {isSubscribed ? t('follow.push.subscribed') : t('follow.push.notSubscribed')}
                </p>
              </div>
            </div>

            {!pushSupported ? (
              <p className="text-xs text-center py-2" style={{ color: muted, fontFamily: sans }}>
                {t('follow.push.notSupported')}
              </p>
            ) : isSubscribed ? (
              <div className="space-y-3">
                {/* State preference */}
                <select
                  value={pushState}
                  onChange={e => {
                    setPushState(e.target.value);
                    pushUpdatePreferences(e.target.value || undefined);
                  }}
                  className="w-full px-3 py-2 rounded-lg text-xs outline-none"
                  style={{ backgroundColor: isDark ? '#0a1628' : '#FAF9F6', border: `1px solid ${border}`, color: ink, fontFamily: sans }}
                >
                  <option value="">{t('follow.allStates')}</option>
                  {US_STATES.map(st => (
                    <option key={st} value={st}>{STATE_NAMES[st] || st} ({st})</option>
                  ))}
                </select>
                {/* Topic toggles */}
                <div className="grid grid-cols-2 gap-2">
                  {(['compliance', 'pricing', 'legislation', 'general'] as PushTopic[]).map(topic => (
                    <label key={topic} className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: ink, fontFamily: sans }}>
                      <input
                        type="checkbox"
                        checked={pushTopics[topic]}
                        onChange={e => {
                          const next = { ...pushTopics, [topic]: e.target.checked };
                          setPushTopics(next);
                          const active = (Object.entries(next) as [PushTopic, boolean][])
                            .filter(([, v]) => v).map(([k]) => k);
                          pushUpdatePreferences(undefined, active);
                        }}
                        className="rounded"
                        style={{ accentColor: green }}
                      />
                      {t(`follow.topics.${topic}`)}
                    </label>
                  ))}
                </div>
                <button
                  onClick={() => { pushUnsubscribe(); trackPushUnsubscribe(); }}
                  disabled={pushLoading}
                  className="w-full py-2 rounded-lg text-xs font-medium transition-opacity hover:opacity-80 disabled:opacity-50"
                  style={{ border: `1px solid ${border}`, color: muted, fontFamily: sans }}
                >
                  {pushLoading ? t('follow.push.unsubscribing') : t('follow.push.unsubscribe')}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <select
                  value={pushState}
                  onChange={e => setPushState(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-xs outline-none"
                  style={{ backgroundColor: isDark ? '#0a1628' : '#FAF9F6', border: `1px solid ${border}`, color: ink, fontFamily: sans }}
                >
                  <option value="">{t('follow.allStates')}</option>
                  {US_STATES.map(st => (
                    <option key={st} value={st}>{STATE_NAMES[st] || st} ({st})</option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    const active = (Object.entries(pushTopics) as [PushTopic, boolean][])
                      .filter(([, v]) => v).map(([k]) => k);
                    pushSubscribe(pushState || undefined, active);
                    trackPushSubscribe(active);
                  }}
                  disabled={pushLoading}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: red, fontFamily: sans }}
                >
                  {pushLoading ? t('follow.push.subscribing') : t('follow.push.enable')}
                </button>
                {pushError && <p className="text-xs" style={{ color: red }}>{pushError}</p>}
                {pushPermission === 'denied' && (
                  <p className="text-xs" style={{ color: muted, fontFamily: sans }}>
                    {t('follow.push.blocked')}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="rounded-xl p-6" style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${gold}15` }}>
                <Mail className="w-5 h-5" style={{ color: emailSubscribed ? green : gold }} />
              </div>
              <div>
                <h3 className="font-semibold text-sm" style={{ fontFamily: sans, color: ink }}>{t('follow.email.title')}</h3>
                <p className="text-xs" style={{ color: muted }}>
                  {emailSubscribed ? t('follow.email.subscribed') : t('follow.email.notSubscribed')}
                </p>
              </div>
            </div>

            {emailSubscribed ? (
              <div className="space-y-3">
                {/* State preference */}
                <select
                  value={emailState}
                  onChange={e => {
                    setEmailState(e.target.value);
                    emailUpdatePreferences(email, e.target.value || undefined);
                  }}
                  className="w-full px-3 py-2 rounded-lg text-xs outline-none"
                  style={{ backgroundColor: isDark ? '#0a1628' : '#FAF9F6', border: `1px solid ${border}`, color: ink, fontFamily: sans }}
                >
                  <option value="">{t('follow.allStates')}</option>
                  {US_STATES.map(st => (
                    <option key={st} value={st}>{STATE_NAMES[st] || st} ({st})</option>
                  ))}
                </select>
                {/* Topic toggles */}
                <div className="grid grid-cols-2 gap-2">
                  {(['compliance', 'pricing', 'legislation', 'general'] as EmailTopic[]).map(topic => (
                    <label key={topic} className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: ink, fontFamily: sans }}>
                      <input
                        type="checkbox"
                        checked={emailTopics[topic]}
                        onChange={e => {
                          const next = { ...emailTopics, [topic]: e.target.checked };
                          setEmailTopics(next);
                          const active = (Object.entries(next) as [EmailTopic, boolean][])
                            .filter(([, v]) => v).map(([k]) => k);
                          emailUpdatePreferences(email, undefined, active);
                        }}
                        className="rounded"
                        style={{ accentColor: green }}
                      />
                      {t(`follow.topics.${topic}`)}
                    </label>
                  ))}
                </div>
                <button
                  onClick={() => { emailUnsubscribe(email); trackEmailUnsubscribe(); }}
                  disabled={emailLoading}
                  className="w-full py-2 rounded-lg text-xs font-medium transition-opacity hover:opacity-80 disabled:opacity-50"
                  style={{ border: `1px solid ${border}`, color: muted, fontFamily: sans }}
                >
                  {emailLoading ? t('follow.push.unsubscribing') : t('follow.push.unsubscribe')}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Email input */}
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={t('follow.email.placeholder')}
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: isDark ? '#0a1628' : '#FAF9F6', border: `1px solid ${border}`, color: ink, fontFamily: sans }}
                />
                {/* State preference */}
                <select
                  value={emailState}
                  onChange={e => setEmailState(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-xs outline-none"
                  style={{ backgroundColor: isDark ? '#0a1628' : '#FAF9F6', border: `1px solid ${border}`, color: ink, fontFamily: sans }}
                >
                  <option value="">{t('follow.allStates')}</option>
                  {US_STATES.map(st => (
                    <option key={st} value={st}>{STATE_NAMES[st] || st} ({st})</option>
                  ))}
                </select>
                {/* Topic toggles */}
                <div className="grid grid-cols-2 gap-2">
                  {(['compliance', 'pricing', 'legislation', 'general'] as EmailTopic[]).map(topic => (
                    <label key={topic} className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: ink, fontFamily: sans }}>
                      <input
                        type="checkbox"
                        checked={emailTopics[topic]}
                        onChange={e => {
                          setEmailTopics(prev => ({ ...prev, [topic]: e.target.checked }));
                        }}
                        className="rounded"
                        style={{ accentColor: green }}
                      />
                      {t(`follow.topics.${topic}`)}
                    </label>
                  ))}
                </div>
                {/* Subscribe button */}
                <button
                  onClick={() => {
                    const active = (Object.entries(emailTopics) as [EmailTopic, boolean][])
                      .filter(([, v]) => v).map(([k]) => k);
                    emailSubscribe(email, emailState || undefined, active);
                    trackEmailSubscribe(active);
                  }}
                  disabled={emailLoading || !email.includes('@')}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: gold, fontFamily: sans }}
                >
                  {emailLoading ? t('follow.push.subscribing') : t('follow.email.subscribe')}
                </button>
                {emailError && <p className="text-xs" style={{ color: red }}>{emailError}</p>}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          C. COMPLIANCE WATCHDOG
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-4xl px-6 pb-12">
        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${border}` }}>
          <div className="p-6" style={{ background: `linear-gradient(135deg, ${isDark ? '#0f1d2f' : navy} 0%, ${isDark ? '#0a1628' : '#001540'} 100%)` }}>
            <div className="flex items-start gap-4 flex-wrap md:flex-nowrap">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${red}33` }}>
                <Shield className="w-5 h-5" style={{ color: red }} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: gold, fontFamily: sans }}>{t('follow.watchdog.label')}</span>
                <p className="text-sm leading-relaxed mb-3 mt-1" style={{ color: 'rgba(255,255,255,0.8)', fontFamily: sans }}>
                  <Trans
                    i18nKey="follow.watchdog.lawSentence"
                    ns="common"
                    values={{ days: DAYS_LAW_ACTIVE.toLocaleString() }}
                    components={{ b: <strong className="text-white" /> }}
                  />
                </p>
                <div className="flex items-center gap-6 flex-wrap mb-3">
                  <div className="text-center">
                    <div className="text-xs uppercase tracking-wider mb-0.5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: sans }}>{t('follow.watchdog.finesOwed')}</div>
                    <div className="text-xl font-bold" style={{ color: red, fontFamily: serif }}>
                      ${(EST_FINE_GAP_TOTAL / 1_000_000_000).toFixed(1)}B+
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs uppercase tracking-wider mb-0.5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: sans }}>{t('follow.watchdog.collected')}</div>
                    <div className="text-xl font-bold" style={{ color: gold, fontFamily: serif }}>
                      ${(CMS_COLLECTED / 1_000_000).toFixed(1)}M
                    </div>
                  </div>
                </div>
                <Link to="/data-sources" className="inline-flex items-center gap-1 text-sm font-semibold transition-opacity hover:opacity-80" style={{ color: gold, fontFamily: sans }}>
                  {t('follow.watchdog.seeNonCompliant')} <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          E. "WHAT WE POST" CONTENT PREVIEW
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-4xl px-6 pb-12">
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: serif, color: ink }}>{t('follow.whatWeTrack.title')}</h2>
        <p className="text-sm mb-6" style={{ color: muted, fontFamily: sans }}>
          {t('follow.whatWeTrack.subtitle')}
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Shield, title: t('follow.whatWeTrack.complianceTitle'), desc: t('follow.whatWeTrack.complianceDesc'), color: red },
            { icon: Scale, title: t('follow.whatWeTrack.legislationTitle'), desc: t('follow.whatWeTrack.legislationDesc'), color: navy },
            { icon: BarChart3, title: t('follow.whatWeTrack.priceComparisonsTitle'), desc: t('follow.whatWeTrack.priceComparisonsDesc'), color: green },
          ].map(item => (
            <div key={item.title} className="rounded-xl p-5" style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${item.color}15` }}>
                <item.icon className="w-4.5 h-4.5" style={{ color: item.color }} />
              </div>
              <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: sans, color: ink }}>{item.title}</h3>
              <p className="text-xs leading-relaxed" style={{ color: muted, fontFamily: sans }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          F. BLOG POSTS PREVIEW
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-4xl px-6 pb-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold" style={{ fontFamily: serif, color: ink }}>{t('follow.insightsGuides')}</h2>
          <Link to="/blog" className="text-sm font-semibold flex items-center gap-1 transition-opacity hover:opacity-80" style={{ color: navy, fontFamily: sans }}>
            {t('actions.viewAll')} <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {blogPosts.slice(0, 3).map(post => {
            const IconComp = ICON_MAP[post.heroIcon] || Newspaper;
            return (
              <Link key={post.slug} to={`/blog/${post.slug}`} className="rounded-xl p-5 transition-all hover:shadow-md group" style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${navy}12` }}>
                  <IconComp className="w-4.5 h-4.5" style={{ color: navy }} />
                </div>
                <h3 className="font-semibold text-sm mb-1 line-clamp-2 group-hover:underline" style={{ fontFamily: sans, color: ink }}>{post.title}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${gold}15`, color: gold }}>
                    {post.audience.split('&')[0].trim()}
                  </span>
                  <span className="text-xs" style={{ color: muted }}>{post.readTime}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          G. CONNECTION TO CARE — Kanban Resource Board
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="w-full px-4 md:px-6 pb-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: serif, color: ink }}>
              {t('follow.care.title')}
            </h2>
            <p className="text-sm max-w-xl mx-auto mb-6" style={{ color: muted, fontFamily: sans }}>
              {t('follow.care.subtitle')}
            </p>

            {/* ── GCP Console-style search ─────────────────────────────── */}
            <div className="relative max-w-2xl mx-auto">
              <div
                className="relative flex items-center transition-all duration-200"
                style={{
                  backgroundColor: isDark ? '#1e293b' : '#fff',
                  border: `1.5px solid ${searchFocused ? (isDark ? '#60a5fa' : '#3b82f6') : border}`,
                  borderRadius: 28,
                  boxShadow: searchFocused
                    ? `0 4px 24px ${isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.12)'}`
                    : `0 1px 6px ${isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.06)'}`,
                }}
              >
                <Search className="w-5 h-5 ml-4 flex-shrink-0" style={{ color: searchFocused ? (isDark ? '#60a5fa' : '#3b82f6') : muted }} />
                <input
                  ref={searchRef}
                  type="text"
                  value={selectedScenario ? selectedScenario.name : scenarioSearch}
                  onChange={e => {
                    setScenarioSearch(e.target.value);
                    setSelectedScenario(null);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => { setSearchFocused(true); setShowSuggestions(true); }}
                  onBlur={() => setSearchFocused(false)}
                  placeholder={t('follow.care.searchPlaceholder')}
                  className="flex-1 px-3 py-3.5 text-sm bg-transparent outline-none"
                  style={{ color: ink, fontFamily: sans }}
                />
                {selectedScenario && (
                  <button
                    onClick={handleClearScenario}
                    className="mr-2 p-1.5 rounded-full transition-colors hover:bg-black/10"
                    style={{ color: muted }}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                {!selectedScenario && scenarioSearch.length === 0 && (
                  <div className="mr-4 flex items-center gap-1 text-xs" style={{ color: muted }}>
                    <Sparkles className="w-3.5 h-3.5" />
                    <span style={{ fontFamily: sans }}>{t('follow.care.scenarioCount', { count: 208 })}</span>
                  </div>
                )}
              </div>

              {/* Autocomplete dropdown */}
              {showSuggestions && scenarioResults.length > 0 && !selectedScenario && (
                <div
                  ref={suggestionsRef}
                  className="absolute z-30 w-full mt-2 rounded-2xl overflow-hidden max-h-80 overflow-y-auto"
                  style={{
                    backgroundColor: isDark ? '#1e293b' : '#fff',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                    boxShadow: `0 8px 32px ${isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.12)'}`,
                  }}
                >
                  <div className="px-4 py-2 border-b" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }}>
                    <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: muted, fontFamily: sans }}>
                      {t('follow.care.suggestionsHeader')}
                    </span>
                  </div>
                  {scenarioResults.map(s => (
                    <button
                      key={s.id}
                      onMouseDown={() => handleSelectScenario(s)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors"
                      style={{
                        color: ink,
                        fontFamily: sans,
                        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}`,
                      }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <Search className="w-3.5 h-3.5 flex-shrink-0" style={{ color: muted }} />
                      <span>{s.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected scenario badge */}
            {selectedScenario && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300"
                style={{
                  backgroundColor: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.08)',
                  color: isDark ? '#93c5fd' : '#2563eb',
                  fontFamily: sans,
                }}
              >
                <Sparkles className="w-3.5 h-3.5" />
                {t('follow.care.showingResources', { name: selectedScenario.name })}
              </div>
            )}
          </div>

          {/* ── Kanban board ─────────────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {CATEGORY_ORDER.map(cat => {
              const items = grouped[cat];
              const colors = KANBAN_COLORS[cat];
              const color = isDark ? colors.dark : colors.light;
              const Icon = KANBAN_ICONS[cat];

              return (
                <div
                  key={cat}
                  className="rounded-xl overflow-hidden flex flex-col"
                  style={{
                    backgroundColor: isDark ? '#0f172a' : '#fafafa',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                    minHeight: 200,
                  }}
                >
                  {/* Column header */}
                  <div
                    className="px-4 py-3 flex items-center justify-between flex-shrink-0"
                    style={{
                      borderBottom: `2px solid ${color}`,
                      backgroundColor: isDark ? `${color}08` : `${color}06`,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" style={{ color }} />
                      <span className="text-xs font-bold uppercase tracking-wider" style={{ color, fontFamily: sans }}>
                        {t(`follow.kanban.${cat}`)}
                      </span>
                    </div>
                    <span
                      className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: `${color}15`, color }}
                    >
                      {items.length}
                    </span>
                  </div>

                  {/* Column body */}
                  <div className="flex-1 p-3 space-y-2 overflow-y-auto" style={{ maxHeight: 480 }}>
                    {items.length === 0 ? (
                      <div className="flex items-center justify-center h-24 text-xs" style={{ color: muted }}>
                        {selectedScenario ? t('follow.care.noMatches') : t('follow.care.searchAScenario')}
                      </div>
                    ) : (
                      items.map((r, i) => (
                        <ResourceCard
                          key={`${r.name}-${i}`}
                          resource={r}
                          color={color}
                          isDark={isDark}
                          index={i}
                          animate={animateResources}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer note */}
          <p className="text-center text-[11px] mt-4 italic" style={{ color: muted, fontFamily: sans }}>
            {t('follow.care.footnote')}
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          H. QUICK LINKS
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-4xl px-6 pb-32">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { to: '/search', icon: Search, label: t('follow.quickLinks.searchPrices') },
            { to: '/hospital-rankings', icon: TrendingUp, label: t('follow.quickLinks.hospitalRankings') },
            { to: '/data-sources', icon: Database, label: t('follow.quickLinks.complianceTracker') },
            { to: '/stories', icon: Users, label: t('follow.quickLinks.patientStories') },
          ].map(link => (
            <Link key={link.to} to={link.to} className="flex flex-col items-center gap-2 rounded-xl p-4 transition-all hover:shadow-md" style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}>
              <link.icon className="w-5 h-5" style={{ color: navy }} />
              <span className="text-xs font-medium text-center" style={{ color: ink, fontFamily: sans }}>{link.label}</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Follow;
